import type { WorkSheet } from "xlsx";

import type { PartnerRow, PartnerDetail } from "./api";

/**
 * Builds and downloads an .xlsx workbook for a single partner's expanded
 * drawer. One tab each for Companies, Sub-accounts, and Purchase orders,
 * carrying the columns shown in the drawer plus a couple of extras
 * (PO term months, company cancel date).
 *
 * Money columns are written as raw numbers so they stay sum-able in Excel;
 * everything else is written as strings matching the on-screen display.
 */
export async function exportPartnerWorkbook(partner: PartnerRow, detail: PartnerDetail) {
  // Lazy-load SheetJS so the ~1MB library is code-split out of the main
  // bundle and only fetched when someone actually exports.
  const XLSX = await import("xlsx");
  const { purchase_orders, companies, sub_links } = detail;

  const companySheet = XLSX.utils.aoa_to_sheet([
    ["Name", "Location", "Active", "Billed first", "Billed last", "Cancel date"],
    ...companies.map((c) => [
      str(c.name),
      [c.city, c.state].filter(Boolean).join(", ") || "",
      c.is_active ? "Active" : "Inactive",
      str(c.billed_first_date),
      str(c.billed_last_date),
      str(c.cancel_date),
    ]),
  ]);
  companySheet["!cols"] = widths([32, 24, 10, 14, 14, 14]);

  const subSheet = XLSX.utils.aoa_to_sheet([
    ["Sub-account", "QB ID", "Type", "Last sub MRR", "Last period", "Effective from"],
    ...sub_links.map((s) => [
      s.name,
      s.sub_qb_id,
      s.is_orphan ? "Billing-only" : "Account",
      s.last_sub_mrr || 0,
      s.last_period ?? "",
      s.effective_period ?? "",
    ]),
  ]);
  currency(XLSX, subSheet, sub_links.length, [3]);
  subSheet["!cols"] = widths([32, 12, 14, 14, 14, 14]);

  const poSheet = XLSX.utils.aoa_to_sheet([
    [
      "Type",
      "Sub-customer",
      "Signed",
      "Effective from",
      "Effective through",
      "Term months",
      "MRR",
      "ARR",
    ],
    ...purchase_orders.map((p) => {
      const mrr = Number(p.mrr_total) || 0;
      return [
        p.contract_type_name === "Replacement Purchase Order" ? "Replacement PO" : "PO",
        str(p.company_name_dba ?? p.company_name),
        str(p.contract_signed_at),
        str(p.effective_from),
        str(p.effective_through),
        Number(p.term_months) || 0,
        mrr,
        mrr * 12,
      ];
    }),
  ]);
  currency(XLSX, poSheet, purchase_orders.length, [6, 7]);
  poSheet["!cols"] = widths([16, 32, 14, 14, 16, 12, 14, 14]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, companySheet, "Companies");
  XLSX.utils.book_append_sheet(wb, subSheet, "Sub-accounts");
  XLSX.utils.book_append_sheet(wb, poSheet, "Purchase orders");

  XLSX.writeFile(wb, `${safeName(partner.account_name)}-partner-export.xlsx`);
}

function str(v: unknown): string {
  return v == null || v === "" ? "" : String(v);
}

function widths(px: number[]) {
  return px.map((wch) => ({ wch }));
}

/** Apply a currency number format to the given zero-based data columns. */
function currency(
  XLSX: typeof import("xlsx"),
  ws: WorkSheet,
  rowCount: number,
  cols: number[],
) {
  for (let r = 1; r <= rowCount; r++) {
    for (const c of cols) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (cell) cell.z = '"$"#,##0';
    }
  }
}

function safeName(name: string): string {
  return name.replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "") || "partner";
}
